import React, {CSSProperties, Dispatch, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {ActionType} from 'typesafe-actions';
import classNames from 'classnames';
import {
  CoreActions,
  CoreDoms,
  ViewMode,
  EditorExperienceType,
  CoreEvent,
} from '@taopix/taopix-core';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {PagesSelector} from '../../../Selectors';
import Page from '../../Page/Page';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {
  HelpTip,
  Horizontal,
  Icon,
  InfoIcon,
  LoadingCircleIcon,
  PlusIcon,
  Vertical,
} from '@taopix/taopix-design-system';
import DeletePageButton from '../../Page/Actions/DeletePageButton';
import {GridViewProps} from './GridView';
import AddPagesButton from '../../Page/Actions/AddPagesButton';
import {useTranslation} from 'react-i18next';
import RetroQuantityButton from '../../RetroPrints/RetroQuantityButton';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import DuplicatePageButton from '../../Page/Actions/DuplicatePageButton';
import {useExperience} from '../../Experience/ExperienceProvider';
import {useTaopix} from '../../Context/TaopixContext';

export type RetroGridViewProps = GridViewProps & {
  min: number;
  max: number;
};

const RetroGridView = ({layout, min, max}: RetroGridViewProps) => {
  const taopix = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation(['retroprint']);
  const {experience} = useExperience();

  const showAddDeleteButtons =
    experience.EDITOR.workArea.gridView.addDeletePage &&
    layout.minimumprintsperproject !== layout.maximumprintsperproject;

  const showDuplicatePageButton =
    experience.EDITOR.workArea.gridView.duplicatePage &&
    layout.minimumprintsperproject !== layout.maximumprintsperproject;

  const [tipOpen, setTipOpen] = useState(false);
  const [error, setError] = useState('');
  const [gridColWidth, setGridColWidth] = useState(ui.state.gridSize);
  const gridLargeScreenTargetColWidth = 300; // Used to calculate a suitable number of cols for current screen and a tile size (gridMaxColWidth) for the core that will fill the available space neatly.
  const gridSmallScreenColCount = 2; // The default column count for small screen
  const gridColGap = 20; // The size of the gap between each column in the grid
  const gridOuterBuffer = 20; // The amount of space to leave on each side of the grid element so it doesn't hit the edge of the workarea
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef<boolean>(true);
  const previousIsSmallScreen = useRef(ui.state.isSmallScreen);

  // create the action dispatcher for the page quantity
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();

  // get the pages from the store
  const pages = useSelector(PagesSelector);

  // calculate the total number of prints across all pages
  const totalPrints = useMemo(() => {
    return pages.reduce((acc: number, curr: PageInterface) => {
      if (0 === curr.qty) {
        acc += 1;
      } else {
        acc += curr.qty;
      }
      return acc;
    }, 0);
  }, [pages]);

  useEffect(() => {
    if (error.length && !ui.state.tips.toolPaletteIntro) {
      if (!tipOpen) {
        setTipOpen(true);
      }
    } else {
      setTipOpen(false);
    }
  }, [error]);

  useEffect(() => {
    if (totalPrints < min || totalPrints > max) {
      return setError(
        t('str_WarningPageCountLimitReached').replace('^0', String(min)).replace('^1', String(max))
      );
    }
  }, [totalPrints, min, max]);

  // Classes for the UL
  const pagesContainerClasses = classNames(
    'self-start',
    'pt-[80rem]',
    'lg:px-[calc(var(--colGap)*1px)]',
    'sm:px-xl',
    'auto-rows-fr',
    'pb-[200rem]'
  );

  const pageToolbarClasses = classNames(
    'flex',
    'justify-center',
    'h-xl',
    'transition-opacity',
    'space-x-xs'
  );

  const tpxCoreAttrs = {
    [CoreDoms.ProjectPagesAttr]: 'true',
  };

  const editorType = useMemo(() => {
    return ui.state.canEditPageLayout ? EditorExperienceType.Retro : EditorExperienceType.Locked;
  }, [ui.state.canEditPageLayout]);

  const onChangeQty = (truthy: boolean, page: PageInterface) => {
    let value = page.qty === 0 ? 1 : page.qty;
    if (truthy) {
      value += 1;
    } else {
      value -= 1;
    }

    // get the current total minus the current page qty
    const runningTotal = totalPrints + page.qty;

    // test the total plus the new page qty against the min/max
    if (runningTotal + value < min || runningTotal + value > max) {
      setError(
        t('str_WarningPageCountLimitReached').replace('^0', String(min)).replace('^1', String(max))
      );
    }

    const prevState = page;
    const newState = {
      ...page,
      qty: value,
    };

    dispatch(CoreActions.updatePage(newState));

    const payload = {
      prevState: prevState,
      newState: newState,
      pageNumber: page.pagenumber,
      isRecordingHistory: true,
      fromBrowser: true,
    };

    PubSub.publish(CoreEvent.HISTORY_ADD, payload);
  };

  const updateGridSize = () => {
    if (gridContainerRef.current) {
      // Calculate the required grid column width by dividing the available width (minus cell padding, column gaps and the outer buffer) by the column count
      const gridContainerWidth = gridContainerRef.current?.clientWidth;
      const gridDefaultColCount = ui.state.isSmallScreen
        ? gridSmallScreenColCount
        : Math.round(gridContainerWidth / gridLargeScreenTargetColWidth);
      const extras = gridColGap * gridDefaultColCount + gridOuterBuffer * 2;
      const newGridColWidth = (gridContainerWidth - extras) / gridDefaultColCount;
      // Update the grid size in the UI context
      ui.dispatch(UserInterfaceActions.setGridSize(newGridColWidth));
    }
  };

  useEffect(() => {
    isFirstRender.current = false;
    updateGridSize();
  }, []);

  useEffect(() => {
    if (!isFirstRender.current && previousIsSmallScreen.current !== ui.state.isSmallScreen) {
      // When changing screen size (but not on the first render), update the grid size after a delay (to help avoid updating while still resizing)
      // and store the changed isSmallScreen state in a ref to allow future comparison
      setTimeout(updateGridSize, 1000);
      previousIsSmallScreen.current = ui.state.isSmallScreen;
    }
  }, [ui.state.isSmallScreen]);

  // Update the grid size state whenever the context is changed by updateGridSize or zoom controls
  useEffect(() => {
    if (ui.state.gridSize !== null && ui.state.gridSize !== gridColWidth) {
      setGridColWidth(ui.state.gridSize);
    }
  }, [ui.state.gridSize]);

  return (
    <div
      id={'gridContainer'}
      className={'flex flex-1 justify-center items-center w-full h-full'}
      ref={gridContainerRef}
    >
      <HelpTip
        open={tipOpen}
        message={error}
        anchorId={'viewerToolbar'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Left}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
        offset={{x: 10, y: 0}}
        className={'max-w-[400rem]'}
        buttonFunction={() => setTipOpen(false)}
        onClose={() => setTipOpen(false)}
        excludeIdFromClickOutside={'workarea'}
        buttonLabel={t('*:str_ButtonOK')}
        icon={<InfoIcon />}
        repositionOnResize
      />
      {gridColWidth === null ? (
        <Icon icon={<LoadingCircleIcon />} size={'xlarge'} className={'mt-[80rem]'} />
      ) : (
        <ul
          {...tpxCoreAttrs}
          className={pagesContainerClasses}
          key={editorType}
          style={
            {
              '--colGap': gridColGap,
            } as CSSProperties
          }
        >
          <>
            {pages.map((page: PageInterface, index: number) => {
              return (
                <li
                  className={classNames('group flex flex-col')}
                  key={`retroview-page-${page.ref}`}
                >
                  <div className={'flex flex-col relative group'}>
                    <Page
                      page={page}
                      layout={layout}
                      editorType={editorType}
                      viewMode={ViewMode.Grid}
                      gridCellMaxWidth={gridColWidth}
                      gridCellMaxHeight={gridColWidth * 1.5}
                      gridColGap={gridColGap}
                      gridRowGap={30}
                      gridCellPadding={0}
                      showErrors={true}
                      showEmptyBoxes={true}
                    >
                      <>
                        <div id={'retro-grid-view-page-toolbar'} className={pageToolbarClasses}>
                          <RetroQuantityButton page={page} onChangeQty={onChangeQty} />
                          {showDuplicatePageButton && (
                            <DuplicatePageButton
                              key={`duplicate-page-button-${page.pagenumber}`}
                              pageNumber={page.pagenumber}
                              buttonProps={{
                                buttonStyle: 'tertiary',
                                hideLabel: true,
                                size: 'small',
                              }}
                            />
                          )}
                          {showAddDeleteButtons && (
                            <DeletePageButton
                              pageNumber={page.pagenumber}
                              buttonProps={{
                                buttonStyle: 'tertiary',
                                hideLabel: true,
                                size: 'small',
                              }}
                              showLabel={false}
                              pageType="print"
                            />
                          )}
                        </div>
                      </>
                    </Page>
                  </div>
                </li>
              );
            })}

            {showAddDeleteButtons && (
              <li className={'flex items-center justify-center pb-xl'}>
                <AddPagesButton
                  buttonProps={{
                    buttonStyle: 'tertiary',
                    stacked: true,
                    startIcon: <PlusIcon />,
                    className: 'w-full h-full',
                  }}
                  pageNumber={layout.lastpage}
                  pageType="print"
                  showCancelButton
                  popoutProps={
                    ui.state.isSmallScreen
                      ? undefined
                      : {
                          anchorOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Middle},
                          transformOrigin: {
                            horizontal: Horizontal.Centre,
                            vertical: Vertical.Middle,
                          },
                          offset: {x: 0, y: 0},
                          arrow: undefined,
                        }
                  }
                />
              </li>
            )}
          </>
        </ul>
      )}
    </div>
  );
};

export default RetroGridView;
